import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { User, ShoppingBag, CreditCard, ArrowLeft, Edit2, Save, X, Package } from "lucide-react";

export default function ParentProfile() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ptaId = searchParams.get('pta');
  
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [parentData, setParentData] = useState<any>(null);
  const [ptaData, setPtaData] = useState<any>(null);
  const [listings, setListings] = useState<any[]>([]);
  const [editing, setEditing] = useState(false);
  const [fullName, setFullName] = useState("");

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (user) {
      fetchParentData();
    }
  }, [user]);

  useEffect(() => {
    if (parentData?.pta_id) {
      fetchPtaData();
      fetchListings();
    }
  }, [parentData?.pta_id]);

  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/portal');
      return;
    }
    setUser(user);
  };

  const fetchParentData = async () => {
    if (!user) return;
    
    const { data, error } = await supabase
      .from('parents')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();
    
    if (error) {
      console.error('Error fetching parent data:', error);
      toast({
        title: "Error",
        description: "Could not load your profile",
        variant: "destructive"
      });
    }
    
    if (data) {
      setParentData(data);
      setFullName(data.full_name || '');
    }
    setLoading(false);
  };

  const fetchPtaData = async () => {
    if (!parentData?.pta_id) return;
    
    const { data } = await supabase
      .from('ptas')
      .select('id, name, school_name, logo_url')
      .eq('id', parentData.pta_id)
      .single();
    
    if (data) {
      setPtaData(data);
    }
  };

  const fetchListings = async () => {
    if (!parentData?.id) return;
    
    const { data } = await supabase
      .from('shop_items')
      .select('*')
      .eq('seller_parent_id', parentData.id)
      .order('created_at', { ascending: false });
    
    if (data) {
      setListings(data);
    }
  };

  const handleSaveProfile = async () => {
    if (!parentData?.id) return;
    
    const { error } = await supabase
      .from('parents')
      .update({ full_name: fullName })
      .eq('id', parentData.id);
    
    if (error) {
      toast({
        title: "Error",
        description: "Could not update your profile",
        variant: "destructive"
      });
    } else {
      toast({
        title: "Profile updated",
        description: "Your changes have been saved"
      });
      setEditing(false);
      fetchParentData();
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/portal');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!parentData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Profile Not Found</CardTitle>
            <CardDescription>You haven't registered as a parent yet.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/portal')}>
              Register as Parent
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-background/95 backdrop-blur sticky top-0 z-50">
        <div className="container flex h-16 items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(parentData?.pta_id ? `/fundraising/${parentData.pta_id}` : '/')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              {ptaData?.logo_url && (
                <img src={ptaData.logo_url} alt="PTA Logo" className="h-8 w-8 rounded object-cover" />
              )}
              <span className="font-semibold">{ptaData?.name || 'Parent Portal'}</span>
            </div>
          </div>
          <Button variant="outline" onClick={handleSignOut}>
            Sign Out
          </Button>
        </div>
      </header>

      <main className="container py-8 max-w-4xl">
        <h1 className="text-3xl font-bold mb-8">My Profile</h1>

        {/* Profile Card */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={user?.user_metadata?.avatar_url} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-xl">
                    {(parentData.full_name || user?.email)?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  {editing ? (
                    <div className="flex items-center gap-2">
                      <Input 
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        className="w-48"
                      />
                      <Button size="icon" variant="ghost" onClick={handleSaveProfile}>
                        <Save className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => {
                        setEditing(false);
                        setFullName(parentData.full_name || '');
                      }}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <CardTitle>{parentData.full_name || 'Parent'}</CardTitle>
                      <Button size="icon" variant="ghost" onClick={() => setEditing(true)}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  <CardDescription>{user?.email}</CardDescription>
                </div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CreditCard className="h-4 w-4" />
                  <span>Credits: <span className="font-semibold text-foreground">{parentData.credit_balance || 0}</span></span>
                </div>
                <Button 
                  variant="link" 
                  size="sm" 
                  className="p-0 h-auto"
                  onClick={() => navigate(`/marketplace/buy-credits?pta=${parentData.pta_id}`)}
                >
                  Buy More Credits
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="listings" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="listings" className="gap-2">
              <ShoppingBag className="h-4 w-4" />
              My Listings
            </TabsTrigger>
            <TabsTrigger value="purchases" className="gap-2">
              <Package className="h-4 w-4" />
              Purchases
            </TabsTrigger>
          </TabsList>

          <TabsContent value="listings" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Your Marketplace Listings</h2>
              <Button onClick={() => navigate(`/marketplace/create?pta=${parentData.pta_id}`)}>
                Create Listing
              </Button>
            </div>

            {listings.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <ShoppingBag className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">You haven't created any listings yet</p>
                  <Button onClick={() => navigate(`/marketplace/create?pta=${parentData.pta_id}`)}>
                    Create Your First Listing
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {listings.map((item) => (
                  <Card key={item.id}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg">{item.name}</CardTitle>
                          <CardDescription>£{item.price?.toFixed(2)}</CardDescription>
                        </div>
                        <Badge variant={item.status === 'active' ? 'default' : 'secondary'}>
                          {item.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground line-clamp-2">{item.description}</p>
                      <div className="flex gap-2 mt-4">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => navigate(`/marketplace?pta=${parentData.pta_id}&item=${item.id}`)}
                        >
                          View
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="purchases" className="mt-6">
            <h2 className="text-xl font-semibold mb-4">Your Purchases</h2>
            <Card>
              <CardContent className="py-12 text-center">
                <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No purchases yet</p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
